import {AfterViewInit, ChangeDetectionStrategy, Component, DestroyRef, ElementRef, inject, Input, OnInit, ViewChild} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {DisabledSelectOptions, SelectOption, SelectOptions} from '@aviary/store/common/common.types';
import {MatOption, MatSelect, MatSelectModule} from '@angular/material/select';
import {ControlRef} from '@aviary/forms/fields/form-fields.types';
import {BaseFormFieldComponent} from '@aviary/forms/fields/base-form-field/base-form-field.component';
import {getExtendedControlFrom} from '@aviary/forms/form.helpers';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';
import {createDeferredInitializer, DeferredInitializerReturnValue} from '@aviary/store/common/initialization.helpers';
import {isArray, isPopulatedArray} from '@aviary/store/common/typing.helpers';
import {provideControlRefTokenFor} from '@aviary/providers/control-ref.provider';
import {MatDivider} from '@angular/material/divider';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {MatTooltip} from '@angular/material/tooltip';
import deepEqual from 'fast-deep-equal';

export type SelectCompareWithFunction<ValueType> = (o1: ValueType | undefined, o2: ValueType | undefined) => boolean;

type DisabledSelectOptionsLookup = Record<string, string>; // key = displayName, value = optional tooltip text

interface InitializationValues<ValueType> {
  fieldName: string;
  formGroup: ExtendedFormGroup;
  options: SelectOptions<ValueType>;
  includeUndefinedOption: boolean;
  initializeAllSelected: boolean;
  compareWith: SelectCompareWithFunction<ValueType>;
  multiple: boolean;
  disabledOptionsLookup: DisabledSelectOptionsLookup;
}

@Component({
  selector: 'mx-select-form-field',
  templateUrl: './select-form-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideControlRefTokenFor(SelectFormFieldComponent)],
  imports: [BaseFormFieldComponent, MatSelectModule, ReactiveFormsModule, MatDivider, MatTooltip]
})
export class SelectFormFieldComponent<ValueType> implements ControlRef, OnInit, AfterViewInit {

  private readonly deferredInitializer: DeferredInitializerReturnValue<InitializationValues<ValueType>> = createDeferredInitializer({
    keysToInitialize: [
      'fieldName', 'formGroup', 'options', 'includeUndefinedOption', 'compareWith', 'multiple', 'initializeAllSelected', 'disabledOptionsLookup'
    ],
    onAllKeysInitialized: this.initialize.bind(this),
    keysToAlwaysUpdate: ['fieldName', 'formGroup', 'options', 'disabledOptionsLookup']
  });

  @ViewChild(MatSelect) public matSelectComponent: MatSelect;
  @ViewChild('selectAllOption') private selectAllOptionElement: MatOption;

  @ViewChild(MatSelect, {read: ElementRef})
  public set matSelectElementRef(elementRef: ElementRef) {
    this.matSelectElement = elementRef?.nativeElement;
  }

  @Input({required: true})
  public set fieldName(fieldName: string) {
    this._fieldName = fieldName;

    this.deferredInitializer.setValue('fieldName', fieldName);
  }

  public get fieldName(): string {
    return this._fieldName;
  }

  @Input({required: true})
  public set formGroup(formGroup: ExtendedFormGroup) {
    this._formGroup = formGroup;

    this.deferredInitializer.setValue('formGroup', formGroup);
  }

  public get formGroup(): ExtendedFormGroup {
    return this._formGroup;
  }

  @Input({required: true})
  public set options(options: SelectOptions<ValueType>) {
    if (isPopulatedArray(this._options) && deepEqual(this._options, options)) return;

    this._options = options;
    this.optionValues = options.map(option => option.value);

    this.deferredInitializer.setValue('options', options);

    // The value of this input affects the initialized value, so recalculate if needed.
    if (this.deferredInitializer.isInitialized()) this.reinitialize();
  }

  public get options(): SelectOptions<ValueType> {
    return this._options;
  }

  @Input()
  public set includeUndefinedOption(includeUndefinedOption: boolean) {
    this._includeUndefinedOption = includeUndefinedOption;

    // If the parent calls this setter, this will trigger the deferred initializer once all other dependent inputs are set.  (If not, see ngOnInit().)
    this.deferredInitializer.setValue('includeUndefinedOption', includeUndefinedOption);

    // The value of this input affects the initialized value, so recalculate if needed.
    if (this.deferredInitializer.isInitialized()) this.reinitialize();
  }

  public get includeUndefinedOption(): boolean {
    return this._includeUndefinedOption;
  }

  @Input()
  public set initializeAllSelected(initializeAllSelected: boolean) {
    this._initializeAllSelected = initializeAllSelected;

    // If the parent calls this setter, this will trigger the deferred initializer once all other dependent inputs are set.  (If not, see ngOnInit().)
    this.deferredInitializer.setValue('initializeAllSelected', initializeAllSelected);

    // The value of this input affects the initialized value, so recalculate if needed.
    if (this.deferredInitializer.isInitialized()) this.reinitialize();
  }

  public get initializeAllSelected(): boolean {
    return this._initializeAllSelected;
  }

  // Use this to specify a deeper comparison when ValueType is an object, array, etc.  Otherwise, we use a replica of Angular Material's default function.
  @Input()
  public set compareWith(compareWith: SelectCompareWithFunction<ValueType>) {
    this._compareWith = compareWith;

    // If the parent calls this setter, this will trigger the deferred initializer once all other dependent inputs are set.  (If not, see ngOnInit().)
    this.deferredInitializer.setValue('compareWith', compareWith);

    // The value of this input affects the initialized value, so recalculate if needed.
    if (this.deferredInitializer.isInitialized()) this.reinitialize();
  }

  public get compareWith(): SelectCompareWithFunction<ValueType> {
    return this._compareWith;
  }

  @Input()
  public set multiple(multiple: boolean) {
    this._multiple = multiple;

    // If the parent calls this setter, this will trigger the deferred initializer once all other dependent inputs are set.  (If not, see ngOnInit().)
    this.deferredInitializer.setValue('multiple', multiple);
  }

  public get multiple(): boolean {
    return this._multiple;
  }

  @Input()
  public set disabledOptions(disabledOptions: DisabledSelectOptions) {
    this._disabledOptionsLookup = disabledOptions.reduce(
      (accumulator, option) => {
        accumulator[option.displayName] = option.tooltip ?? '';

        return accumulator;
      },
      {}
    );

    // If the parent calls this setter, this will trigger the deferred initializer once all other dependent inputs are set.  (If not, see ngOnInit().)
    this.deferredInitializer.setValue('disabledOptionsLookup', this._disabledOptionsLookup);

    // The value of this input affects the initialized value, so recalculate if needed.
    if (this.deferredInitializer.isInitialized()) this.reinitialize();
  }

  @Input() public overrideLabel: string;
  @Input() public optionsPanelClass = '';
  @Input() public enabled = true;
  @Input() public triggerLabel = '';
  @Input() public hintLabel: string = '';
  @Input() public includeSelectAllOption = false;
  @Input() public selectAllLabel: string = '';

  // It's tempting to rename this to "formControl", but that's a reserved name and causes confusing errors.  So don't.
  public control: ExtendedFormControl<ValueType | ValueType[] | undefined> = new ExtendedFormControl<ValueType | ValueType[] | undefined>(undefined);

  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private matSelectElement?: HTMLElement;
  private _fieldName: string;
  private _formGroup: ExtendedFormGroup;
  private _options: SelectOptions<ValueType> = [];
  private optionValues: ValueType[] = [];
  private _includeUndefinedOption: boolean = true;
  private _initializeAllSelected: boolean = false;
  private _compareWith: SelectCompareWithFunction<ValueType> = (a: ValueType | undefined, b: ValueType | undefined) => a === b;
  private _multiple = false;
  private _disabledOptionsLookup: DisabledSelectOptionsLookup = {};

  public ngOnInit(): void {
    // If the optional includeUndefinedOption setter wasn't called before we got here, then the deferred initializer hasn't had a chance to initialize yet...
    if (!this.deferredInitializer.isInitialized()) {
      // ...so set the values needed to trigger it now.
      this.deferredInitializer.setValue('includeUndefinedOption', this._includeUndefinedOption);
      this.deferredInitializer.setValue('initializeAllSelected', this._initializeAllSelected);
      this.deferredInitializer.setValue('compareWith', this._compareWith);
      this.deferredInitializer.setValue('multiple', this._multiple);
      this.deferredInitializer.setValue('disabledOptionsLookup', this._disabledOptionsLookup);
    }
  }

  public ngAfterViewInit(): void {
    if (this._multiple && this.includeSelectAllOption && this.selectAllOptionElement) {
      this.updateAllSelectElement();
      this._formGroup.valueChanges.pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => {
        this.updateAllSelectElement();
      });
    }
  }

  public isDisabled(option: SelectOption<ValueType>): boolean {
    return option.displayName in this._disabledOptionsLookup;
  }

  public disabledOptionTooltipFor(option: SelectOption<ValueType>): string {
    return this._disabledOptionsLookup[option.displayName] ?? '';
  }

  public open(): void {
    this.matSelectComponent.open();

    setTimeout(
      () => this.matSelectElement?.focus()
    );
  }

  private initialize(): void {
    this.control = getExtendedControlFrom(this._formGroup, this._fieldName);
    if (this._multiple && this._initializeAllSelected) {
      this.control.setValue(this.optionValues);
      this.control.resetInitialValueWithCurrentValue();
    } else {
      this.initializeValueToFirstOptionIfNoneMatched();
    }
  }

  private reinitialize(): void {
    if (this._multiple && this._initializeAllSelected) {
      this.control.setValue(this.optionValues);
    } else {
      this.initializeValueToFirstOptionIfNoneMatched(false);
    }
  }

  private initializeValueToFirstOptionIfNoneMatched(resetInitialValue: boolean = true): void {
    const enabledOptionValues: ValueType[] =
      this._options
        .filter(option => !this.isDisabled(option))
        .map(option => option.value);

    // If the initial value matches an enabled option, we're good.  Otherwise, update the value to match the first option (which is perhaps undefined).
    if (
      !this._multiple
      && isPopulatedArray(enabledOptionValues)
      && !enabledOptionValues.some(option => this._compareWith(option, isArray(this.control.value) ? undefined : this.control.value ?? undefined))
    ) {
      this.control.setValue(this._includeUndefinedOption ? undefined : enabledOptionValues[0], {emitEvent: false});
      if (resetInitialValue) this.control.resetInitialValueWithCurrentValue();
    }
  }

  private allSelected(): boolean {
    const selectedValues = isArray(this.control.value) ? this.control.value : [];
    return selectedValues.length > 0 && this.optionValues.every(optionValue =>
      selectedValues.some(selectedValue => this._compareWith(optionValue, selectedValue))
    );
  }

  private updateAllSelectElement(): void {
    if (this.allSelected()) {
      this.selectAllOptionElement.select(false);
    } else {
      this.selectAllOptionElement.deselect(false);
    }
  }

  public toggleAll(): void {
    if (this.allSelected()) {
      this.control.patchValue([]);
    } else {
      this.control.patchValue([...this.optionValues]);
    }
  }
}
